package ui.campaign.detailv2.editor.wrapper

import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.TextFieldValue
import common.frontend.api.Io
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.plus
import org.ssoft.common.tools.EMPTY
import org.ssoft.common.tools.MAX
import org.ssoft.common.tools.nowDate
import ssoft.frontend.generated.resources.*
import tools.*
import ui.widget.DropDownItem
import kotlin.math.roundToInt
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.milliseconds

class CampaignWrapperEGeneral(
    val data: Io.Campaign
) : ImplCampaignWrapperE {

    private var volumeEnabledField = data.volume > 0F
    private val hourInMs = 1.hours.inWholeMilliseconds.toFloat()
    private var _impressionSpace = calculateSpace()

    val slotsValues = CounterPicker.create(data.settings.companyCount, data.settings.companyDuration.milliseconds)


    init {
        val now = Clock.nowDate()
        if (data.start.date < now) {
            data.start = (LocalDateTime(now, LocalTime.EMPTY))
        }
        if (data.end.date < now) {
            data.end = LocalDateTime(now.plus(30, DateTimeUnit.DAY), LocalTime.MAX)
        }
    }

    data class CounterPicker(
        val value: Int,
        val duration: Duration,
        override val label: String = "$value slot${if (value > 1) "s" else ""},  ${duration * value}",
        override val image: ImageVector? = null
    ) : DropDownItem {

        companion object {
            fun create(count: Int, duration: Duration): List<CounterPicker> {
                return (1..count)
                    .map { CounterPicker(it, duration) }
            }
        }
    }


    val name = EFieldTextString(
        property = data::name,
        condition = { it -> it.trim().takeIf { it.isNotEmpty() } })

    val isCommercial = EFieldRegularBoolean(
        property = data::isCommercial,
        onChange = {
            validateCom(it)
            if (!it) {
                if (impressionCount.err) {
                    impressionCount.set(TextFieldValue(data.settings.companyCount.toString()))
                }
                if (impressionDuration.err) {
                    impressionDuration.set(TextFieldValue(data.settings.companyDuration.milliseconds.inWholeSeconds.toString()))
                }
            }
        })

    val isNonCommercial = EFieldRegularBoolean(
        property = data::isNonCommercial,
        onChange = {
            validateNonCom(it)
        })

    val volume = EFieldDualIntAsFloat(
        property = data::volume
    )

    val dtStart = EFieldRegularDateTime(
        property = data::start
    )

    val dtEnd = EFieldRegularDateTime(
        property = data::end
    )


    val slotsCount: EFieldRegularInt = EFieldRegularInt(
        property = data.settings::campaignCount
    )

    val impressionCount: EFieldTextInt = EFieldTextInt(
        property = data.settings::campaignCount,
        condition = { it.trim().toIntOrNull()?.takeIf { it > 0 } },
        onChange = {
            impressionSpace.set(calculateSpace().toString().toTextFieldValue())
        })

    val impressionDuration: EFieldTextMsAsSec = EFieldTextMsAsSec(
        property = data.settings::campaignDuration,
        condition = { it.trim().toLongOrNull()?.takeIf { it > 0 } },
        onChange = {
            impressionSpace.set(calculateSpace().toString().toTextFieldValue())
        })


    val impressionSpace: EFieldTextFloat = EFieldTextFloat(
        property = ::_impressionSpace,
        condition = { it.trim().toFloatOrNull()?.takeIf { it in 0F..100F } },
        onChange = { newSpace ->
            calculateImpression(newSpace)
                ?.takeIf { it != impressionCount.state }
                ?.toString()
                ?.toTextFieldValue()
                ?.also { impressionCount.set(it) }
        })

    fun validateNonCom(newValue: Boolean): Boolean {
        return if (!newValue && isCommercial.state == false) {
            isCommercial.set(true)
            false
        } else true
    }

    fun validateCom(newValue: Boolean): Boolean {
        return if (!newValue && isNonCommercial.state == false) {
            isNonCommercial.set(true)
            false
        } else true
    }

    private val fieldsErrAble by lazy {
        listOf(name, impressionCount, impressionDuration, impressionSpace)
    }

    private val fields by lazy {
        listOf(
            isCommercial, isNonCommercial,
            dtStart, dtEnd,
            volume,
            slotsCount
        ) + fieldsErrAble
    }

    override val err
        get() = fieldsErrAble.any { it.err }


    private fun calculateImpression(space: Float): Int? {
        return kotlin.runCatching {
            (((space / 100F) * hourInMs) / data.settings.campaignDuration.toFloat()).roundToInt()
        }.getOrNull()
    }

    private fun calculateSpace(): Float {
        return runCatching {
            @Suppress("ComplexRedundantLet", "SimpleRedundantLet")
            ((data.settings.campaignDuration * data.settings.campaignCount).toFloat())
                .let { it / hourInMs }
                .let { it * 10000F }
                .let { it.roundToInt() }
                .let { it.toFloat() / 100F }
        }.getOrNull() ?: 0F
    }

    override suspend fun extractErr(): String? = buildString {
        if (name.err) {
            appendLine(Res.string.layer_top_detail_editor__err_general_no_name.await())
        }
        if (impressionCount.err) {
            appendLine(Res.string.layer_top_detail_editor__err_general_zero_counter.await())
        }
        if (impressionDuration.err) {
            appendLine(Res.string.layer_top_detail_editor__err_general_zero_duration.await())
        }
        if (impressionSpace.err) {
            appendLine(Res.string.layer_top_detail_editor__err_general_zero_space.await())
        }
    }.trim().takeIf { it.isNotEmpty() }
}